import { getAnalytics } from "@tm/insurance/analytics";
import {
  getSelectedCountriesFromLocalStorage,
  initializePageTracking,
} from "@tm/insurance/analytics/xdm";
import { convertUnixToDate, getFormFieldErrorMsg } from "@tm/insurance/utils";
import { constants } from "@tm/isomorphic-utils";
import { BreadcrumbComponent } from "@tm/static";
import { usePlanType } from "@tm/ui";
import { useBreakpointV2 } from "@tm/ui-widgets";
import { ClientError } from "@tm/utility";
import { trackEvent } from "libs/utility/src/lib/analytics/XDMAnalytics/xdmEvents";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import { MYPARTNER_COMPONENT_MAPPER } from "../../../components/mypartner";
import WebToast from "../../../components/Toast/WebToast";
import {
  HeadComp,
  LeftComp,
  MainContainer,
  RightComp,
  TravellerDetailLayoutV2,
  WebToastWrap,
} from "./InsuranceTravelDetailsV2.styles";
export interface IPrimaryContact {
  email: string;
  mobile: string;
}
export interface ISecondaryContactV2 {
  name: string;
  dob: string;
  gender: string;
  isSelected: boolean;
  isEdited?: boolean;
  isSavedTraveller?: boolean;
  passport?: string;
  nomineeName?: string;
}

export interface ITravellerDetailsFormV2 {
  travellerData: ISecondaryContactV2[];
  primaryContactData: IPrimaryContact;
  tncs: boolean[];
}

interface TravellerError {
  index: number;
  errorType: string;
}

export interface ISecondaryContactFields {
  name?: { isRequired?: boolean };
  dob?: { isRequired?: boolean };
  gender?: { isRequired?: boolean };
  passport?: { isRequired?: boolean };
  nomineeName?: { isRequired?: boolean };
}

export type InsTravDetailV2Props = {
  travellerDetailData: {
    components: {
      id: string;
      data: any;
    }[];
    pageData: any;
  };
  // Error handling props (same as InsTravDetailProps)
  setActiveModal: (modal: string) => void;
  isLoading: boolean;
  checkoutError?: any;
  handleTravellerPayment: (formData: any) => Promise<void>;
  onFormDataChange?: (formData: any) => void;
  getSelectedCountry?: (selectedCountry: any) => void;
  firstErrorIndex: number;
  setFirstErrorIndex: (index: number) => void;
  onSetError?: any;
  // MyPartner specific props
  selectedCountry?: { countryCode: string };
  travellerCount?: number;
};

const InsTravDetailV2 = ({
  travellerDetailData: InsTravDetailV2,
  handleTravellerPayment,
  onFormDataChange,
  selectedCountry,
  getSelectedCountry,
  setActiveModal,
  isLoading: externalIsLoading,
  checkoutError,
  firstErrorIndex,
  setFirstErrorIndex,
  onSetError,
}: InsTravDetailV2Props) => {
  const [secondaryContactFields, setSecondaryContactFields] =
    useState<ISecondaryContactFields>({});
  const { components, pageData } = InsTravDetailV2;
  const { travellerCount, errorToasts } = pageData || {};
  const {
    formErrorToast: pageFormErrorToast,
    addErrorToast: pageAddErrorToast,
  } = errorToasts || {};
  const { formErrorToast, addErrorToast } = constants;

  // Analytics and plan type
  const analytics = getAnalytics();

  useEffect(() => {
    analytics?.trackPageLoadEvent({
      pageName: "REVIEW",
      experiment: pageData?.experiment,
    });
    const queryParams = new URLSearchParams(window.location.search);
    const selectedCountries = getSelectedCountriesFromLocalStorage(
      queryParams.get("countries")?.split(",") || []
    );

    const segment = queryParams.get("segment");
    const startDate = queryParams.get("startDate");
    const endDate = queryParams.get("endDate");
    let start, end;
    if (startDate && endDate) {
      start = convertUnixToDate(Number(startDate));
      end = convertUnixToDate(Number(endDate));
    }
    const travellers = queryParams.get("travellers");
    const subpageName = queryParams.get("type");
    const productId = queryParams.get("productId");
    initializePageTracking(
      {
        page_context: {
          page_name: "review",
          page_type: "funnel",
          funnel_step: "review",
          ...(subpageName && { subpage_name: `${subpageName}_mypartner` }),
        },
        event_detail: {
          ...(productId && {
            components: {
              product_list: [
                {
                  id: productId ?? "",
                },
              ],
            },
          }),
        },
      },
      {
        countries: selectedCountries,
        journeyType: segment || "",
        travellers: travellers ? parseInt(travellers) : 1,
        fromDateTime: start ? start.getTime() / 1000 : 0,
        toDateTime: end ? end.getTime() / 1000 : 0,
        isStoreSearchContext: true,
      }
    );
  }, []);
  const { type } = usePlanType();
  const device = useBreakpointV2();

  // Use external loading state if provided, otherwise use local state
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const isLoading =
    externalIsLoading !== undefined ? externalIsLoading : localIsLoading;

  // Validation state
  const [isSelectedError, setIsSelectedError] = useState<boolean>(false);
  const [isFieldError, setIsFieldError] = useState<boolean>(false);

  const methods = useForm<ITravellerDetailsFormV2>({
    defaultValues: {
      travellerData: Array.from({
        length: pageData?.travCount ?? 2, // TODO: get length of travCount and savedTravellers length
      }).map(() => ({
        name: "",
        dob: "",
        gender: "",
        passport: "",
        nomineeName: "",
        isSelected: false,
      })),
      primaryContactData: {
        email: "",
        mobile: "",
      },
      tncs: Array.from({ length: 1 }).map(() => true), // TODO: get length of tncs
    },
    mode: "onTouched",
  });

  const {
    control,
    getValues,
    setValue,
    getFieldState,
    setError,
    handleSubmit,
    trigger,
  } = methods;

  const formValues = getValues();

  // Watch traveller data to track selected count
  const travellerData = useWatch({
    control,
    name: "travellerData",
  });

  // Calculate selected count
  const selectedCount = useMemo(
    () =>
      travellerData?.filter((traveller) => traveller.isSelected)?.length ?? 0,
    [travellerData]
  );

  // References for scrolling to errors
  const travellerRef = useRef<HTMLInputElement>(null);
  const primaryContactRef = useRef<HTMLInputElement>(null);
  const tncRef = useRef<HTMLInputElement>(null);

  // State to control which traveller is expanded (for collapse/expand animation)
  const [expandedTravellerIndex, setExpandedTravellerIndex] = useState(-1);

  // Reusable method to scroll to error traveller
  const scrollToErrorTraveller = (errorIndex: number, delay: number = 300) => {
    setTimeout(() => {
      // Try to find the specific traveller element by data attribute
      const errorTravellerElement = document.querySelector(
        `[data-traveller-index="${errorIndex}"]`
      );
      if (errorTravellerElement) {
        errorTravellerElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      } else {
        // Fallback to travellerRef
        travellerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, delay);
  };

  // Handle checkout errors - throw as ClientError like in AddTravellerDetails
  if (checkoutError && typeof window !== "undefined") {
    throw new ClientError(checkoutError);
  }

  // Handle firstErrorIndex - collapse all travellers and focus on error traveller
  useEffect(() => {
    if (firstErrorIndex !== undefined && firstErrorIndex >= 0) {
      // Collapse all travellers (including the error one) to show error in collapsed format
      setExpandedTravellerIndex(-1);

      // Scroll to error traveller after collapse animation
      scrollToErrorTraveller(firstErrorIndex, 300);
    }
    if (setFirstErrorIndex) {
      setFirstErrorIndex(-1);
    }
  }, [firstErrorIndex, setFirstErrorIndex]);

  // Handle onSetError - set form errors when onSetError prop changes
  useEffect(() => {
    if (onSetError) {
      setError(onSetError?.errTrype, onSetError?.errName);
    }
  }, [onSetError, setError]);

  const handleErrorToast = () => {
    setTimeout(() => {
      setIsSelectedError(false);
      setIsFieldError(false);
    }, 3000);
  };

  const handleTncCheckbox = (index: number) => {
    const value = formValues.tncs[index];
    analytics?.trackTravDetailsEvents({
      actionType: `disclaimer_${value ? "unchecked" : "checked"}`,
    });
    setValue(`tncs.${index}`, !value);
    trigger(`tncs.${index}`);
  };

  type FormFieldType = "name" | "dob" | "gender" | "passport" | "nomineeName";

  const filterTravellers = async (
    selected: number[],
    errors: TravellerError[]
  ) => {
    let targetPosition = 0;
    const newIndices: any = new Array(travellerData.length);
    const errorIndices = errors.map((err) => err.index);

    travellerData.forEach((traveller, index) => {
      if (errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    travellerData.forEach((traveller, index) => {
      if (selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    travellerData.forEach((traveller, index) => {
      if (!selected.includes(index) && !errorIndices.includes(index)) {
        newIndices[index] = targetPosition;
        targetPosition++;
      }
    });

    const newArray: any = [];
    newIndices.forEach((indexVal: number, index: number) => {
      newArray[indexVal] = getValues(`travellerData.${index}`);
    });

    // Replace the array with the reordered one
    setValue("travellerData", newArray);
    const isValid = await trigger("travellerData");

    errors.forEach(({ index, errorType }, ind) => {
      const fieldKey = `travellerData.${ind}.${errorType}` as
        | `travellerData.${number}.name`
        | `travellerData.${number}.dob`
        | `travellerData.${number}.gender`
        | `travellerData.${number}.passport`
        | `travellerData.${number}.nomineeName`;

      // errorTrackEvent({
      //   error_details_list: [{
      //     error_message: getFormFieldErrorMsg(errorType as FormFieldType),
      //     error_source: fieldKey,
      //     error_severity: 3,
      //   }],
      // })

      setError(fieldKey, {
        message: getFormFieldErrorMsg(errorType as FormFieldType),
      });
    });
  };

  // Simplified payment flow without login logic
  const onSubmit: SubmitHandler<ITravellerDetailsFormV2> = async (formData) => {
    // Only set local loading state if no external loading management
    if (externalIsLoading === undefined) {
      setLocalIsLoading(true);
    }
    try {
      // Pass form data to parent component for handling
      if (onFormDataChange) {
        onFormDataChange(formData);
      }

      // Pass selected country to parent
      if (getSelectedCountry && selectedCountry) {
        getSelectedCountry(selectedCountry);
      }

      // Track analytics events
      analytics?.trackTravDetailsEvents(
        { actionType: "traveller_name_filled" },
        "pageView",
        type
      );
      analytics?.trackTravDetailsEvents(
        { actionType: "contact_details_filled" },
        "pageView",
        type
      );
      analytics?.trackTravDetailsEvents(
        { actionType: "dob_selected" },
        "pageView",
        type
      );
      analytics?.trackTravDetailsEvents(
        { actionType: "gender_selected" },
        "pageView",
        type
      );
      // Since user is always logged in, proceed directly to payment
      if (handleTravellerPayment) {
        await handleTravellerPayment(formData);
      }
    } catch (error) {
      console.error("Payment processing error:", error);
    } finally {
      // Only manage local loading state if no external loading management
      if (externalIsLoading === undefined) {
        setLocalIsLoading(false);
      }
    }
  };

  // Handle form validation errors
  const onSubmitError: SubmitErrorHandler<ITravellerDetailsFormV2> = (
    errors
  ) => {
    // Only manage local loading state if no external loading management
    if (externalIsLoading === undefined) {
      setLocalIsLoading(false);
    }

    if (!isFieldError) {
      setIsFieldError(true);
      handleErrorToast();
    }

    // Handle setting firstErrorIndex like in AddTravellerDetails with improved animation
    if (errors.travellerData?.length) {
      const errorIndex = errors.travellerData?.findIndex?.((error) => error);
      if (errorIndex != null && errorIndex >= 0 && setFirstErrorIndex) {
        // Collapse all travellers to show error in collapsed format
        setExpandedTravellerIndex(-1);
        setFirstErrorIndex(errorIndex);

        // Scroll to error traveller after collapse animation
        scrollToErrorTraveller(errorIndex, 350);
      }
    } else if (
      errors.primaryContactData?.email ||
      errors.primaryContactData?.mobile
    ) {
      // Add animation delay for primary contact errors
      setTimeout(() => {
        primaryContactRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "center",
        });
      }, 150);
    } else if (errors.tncs?.length) {
      // Add animation delay for TnC errors
      setTimeout(() => {
        tncRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "center",
        });
      }, 150);
    }
  };

  // Main validation and buy handler like in AddTravellerDetails
  const handleBuy = (fromBenefitModal: boolean = false) => {
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "buy_clicked",
      },
    });
    analytics?.trackTravDetailsEvents(
      { actionType: "buy_clicked" },
      "pageView",
      type
    );

    if (selectedCount !== travellerCount) {
      if (!isFieldError) {
        setIsFieldError(true);
        if (setFirstErrorIndex) {
          setFirstErrorIndex(0);
        }
        // Collapse all travellers to show error in collapsed format
        setExpandedTravellerIndex(-1);
        // Scroll to first traveller after collapse animation
        scrollToErrorTraveller(0, 300);
        handleErrorToast();
      }
    } else {
      const formValues = getValues();
      const selected: number[] = [];
      const errorArr: TravellerError[] = [];

      formValues?.travellerData?.map((item, index) => {
        const {
          name: fullName,
          dob: dobValue,
          gender: genderType,
          passport: passportType,
          nomineeName: nomineeNameType,
          isSelected,
        } = item || {};

        if (isSelected) {
          selected.push(index);
        }
        const fieldState = getFieldState(`travellerData.${index}`);
        const { isTouched } = fieldState;

        if (!isTouched) {
          if (secondaryContactFields?.name?.isRequired && !fullName) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "name",
              });
            }
          } else if (secondaryContactFields?.dob?.isRequired && !dobValue) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "dob",
              });
            }
          } else if (
            secondaryContactFields?.gender?.isRequired &&
            !genderType
          ) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "gender",
              });
            }
          } else if (
            secondaryContactFields?.passport?.isRequired &&
            !passportType
          ) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "passport",
              });
            }
          } else if (
            secondaryContactFields?.nomineeName?.isRequired &&
            !nomineeNameType
          ) {
            if (selected.includes(index)) {
              errorArr.push({
                index,
                errorType: "nomineeName",
              });
            }
          }
        }
      });
      filterTravellers(selected, errorArr);

      // If there are errors, collapse all travellers and focus on first error
      if (errorArr.length > 0 && setFirstErrorIndex) {
        const firstErrorIndex = errorArr[0]?.index ?? 0;
        setExpandedTravellerIndex(-1); // Collapse all to show error in collapsed format
        setFirstErrorIndex(firstErrorIndex);

        // Scroll to error traveller after collapse animation
        scrollToErrorTraveller(firstErrorIndex, 350);
      }

      handleSubmit(onSubmit, onSubmitError)();
    }
  };

  type Position = "top" | "left" | "right";
  const templateIdCounter: Record<Position, Record<string, number>> = {
    top: {},
    left: {},
    right: {},
  };

  return (
    <TravellerDetailLayoutV2 id="pageId">
      <HeadComp>
        <BreadcrumbComponent />
        {components?.map((card) => {
          const CardComponent = MYPARTNER_COMPONENT_MAPPER["top"][card.id];
          if (CardComponent) {
            templateIdCounter.top[card.id] =
              templateIdCounter.top[card.id] || 0;
            templateIdCounter.top[card.id]++;
            return (
              <CardComponent
                key={`top-${card.id}-${templateIdCounter.top[card.id]}`}
                {...card.data}
              />
            );
          }
          return null;
        })}
      </HeadComp>
      <MainContainer>
        <LeftComp>
          <FormProvider {...methods}>
            {components?.map((card) => {
              const CardComponent = MYPARTNER_COMPONENT_MAPPER["left"][card.id];
              if (CardComponent) {
                templateIdCounter.left[card.id] =
                  templateIdCounter.left[card.id] || 0;
                templateIdCounter.left[card.id]++;
                return (
                  <CardComponent
                    key={`left-${card.id}-${templateIdCounter.left[card.id]}`}
                    {...card.data}
                    handleTncCheckbox={handleTncCheckbox}
                    setValue={setValue}
                    formValues={formValues}
                    isLoading={isLoading}
                    travellerRef={travellerRef}
                    firstErrorIndex={firstErrorIndex}
                    selectedCount={selectedCount}
                    travellerCount={travellerCount}
                    setSecondaryContactFields={setSecondaryContactFields}
                    primaryContactRef={primaryContactRef}
                    tncRef={tncRef}
                    onPayment={handleBuy}
                    expandedTravellerIndex={expandedTravellerIndex}
                    setExpandedTravellerIndex={setExpandedTravellerIndex}
                    visibleTravellersCount={
                      pageData?.visibleTravellersCount || 5
                    }
                  />
                );
              }
              return null;
            })}
          </FormProvider>
        </LeftComp>
        <RightComp>
          {components?.map((card) => {
            const CardComponent = MYPARTNER_COMPONENT_MAPPER["right"][card.id];
            if (CardComponent) {
              templateIdCounter.right[card.id] =
                templateIdCounter.right[card.id] || 0;
              templateIdCounter.right[card.id]++;
              return (
                <CardComponent
                  key={`right-${card.id}-${templateIdCounter.right[card.id]}`}
                  {...card.data}
                  onPayment={handleBuy}
                  isLoading={isLoading}
                  formValues={formValues}
                  travellerRef={travellerRef}
                  firstErrorIndex={firstErrorIndex}
                  selectedCount={selectedCount}
                  travellerCount={travellerCount}
                  setSecondaryContactFields={setSecondaryContactFields}
                  primaryContactRef={primaryContactRef}
                  tncRef={tncRef}
                  expandedTravellerIndex={expandedTravellerIndex}
                  setExpandedTravellerIndex={setExpandedTravellerIndex}
                />
              );
            }
            return null;
          })}
        </RightComp>
      </MainContainer>

      {isSelectedError && (
        <WebToast
          toastTxt={`Already selected ${travellerCount} ${
            travellerCount === 1 ? "traveller" : "travellers"
          }, Remove before adding one.`}
          position="fixed"
          right={"50px"}
          margin={device === "lg" ? "0px 0px 0px 28px" : ""}
          height="40px"
          bottom={device === "sm" ? "70px" : "50px"}
        />
      )}

      {isFieldError && (
        <WebToastWrap>
          <WebToast
            type="error"
            position="fixed"
            right={"50px"}
            bottom={device === "sm" ? "70px" : "50px"}
            toastTxt={
              selectedCount === travellerCount
                ? pageFormErrorToast?.message || formErrorToast
                : pageAddErrorToast?.message || addErrorToast
            }
            color="#6B4300"
          />
        </WebToastWrap>
      )}
    </TravellerDetailLayoutV2>
  );
};

export default InsTravDetailV2;
